import { checkIfOpen, getNextTimeEvent } from 'wsm-lsp-utilities';

/**
 * Builds an array of LocationTile objects from a locationsResponse array.
 *
 * @param {LocationTile[]} locationsResponse - The array of location objects to transform.
 * @param {string} locationDetailPagePart - The URL part for location detail pages.
 * @param {string} [dealerPhotoBaseUrl] - The account-assets host (scheme + host,
 *   no trailing path), resolved from the `ddc-account-assets` <Foo>Keeper
 *   endpoint by ws-locations-data and read via getDealerPhotoBaseUrl(). The
 *   `/dealerphoto/<id>.jpg` path is appended here. When blank, no dealer photo
 *   is set (wsm-tiles renders its placeholder).
 * @returns {LocationTileData[]} Array of LocationTile objects.
 */
export const buildLocationTiles = (
	locationsResponse,
	locationDetailPagePart,
	dealerPhotoBaseUrl = ''
) => {
	if (!Array.isArray(locationsResponse)) return [];
	return locationsResponse.map((location) => {
		const nameComaIndex = location.name.indexOf(',');
		const name =
			nameComaIndex !== -1
				? location.name.slice(0, nameComaIndex)
				: location.name;
		const now = new Date();
		const currentDay = now
			.toLocaleString('en-us', {
				weekday: 'long',
				timeZone: location.timeZoneId
			})
			.toLowerCase();
		const currentTime = now.toLocaleTimeString('en-us', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
			timeZone: location.timeZoneId
		});
		// Check if the location is open now
		const isOpen = checkIfOpen(
			location.hours,
			currentDay,
			currentTime.replace(/\s/g, '')
		);
		const openCloseText = getNextTimeEvent(
			location.hours,
			currentDay,
			currentTime.replace(/\s/g, '')
		);
		const href = `${locationDetailPagePart}/${encodeURIComponent(
			name.replace(/\s+/g, '-')
		)}/${encodeURIComponent(location.city.replace(/\s+/g, '-'))}.htm`;

		// Build the dealer photo URL from the account ID and the
		// <Foo>Keeper-resolved account-assets host (see src/server/store.js).
		// Falls back to null so wsm-tiles Media uses its placeholder when the id or host
		// is missing.
		const photoHost = dealerPhotoBaseUrl.replace(/\/+$/, '');
		const dealershipImgUrl =
			location.id && photoHost
				? `${photoHost}/dealerphoto/${location.id}.jpg`
				: null;

		return {
			location: {
				...location,
				address1: '',
				name,
				distance: location.distance.toFixed(0), // truncate distance to 0 decimal places
				isOpen,
				openCloseText,
				dealershipImgUrl,
				href
			},
			displayType: 'media'
		};
	});
};
